import { FormApi, FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-calculate";
import formatString from "format-string-by-pattern";
import { get, set } from "lodash";
import React from "react";
import { Form as FinalForm, FormSpy } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Col, Container, Row, Spinner } from "reactstrap";
import CollapsibleCard from "src/components/CollapsibleCard/CollapsibleCard";
import CheckboxField from "src/components/Form/CheckboxFieldV2/CheckboxFieldV2";
import RadioButton from "src/components/Form/RadioButtonV2/RadioButton";
import SimpleDatePickerField from "src/components/Form/SimpleDatePickerFieldV2/SimpleDatePickerField";
import CustomIcon, { IconName } from "src/components/Icon/Icon";
import {
  fetchAnalyzerPreferenceOptions,
  fetchBrokerAnalyzerPreference,
  saveBrokerAnalyzerPreference,
} from "src/store/reducers/brokerAnalyzerPreferences";
import { Container as DetailContainer } from "../../components/Container/Container";
import SingleSelectField from "../../components/Form/SingleSelectFieldV2/SingleSelectField";
import TextField from "../../components/Form/TextFieldV2/TextFieldV2";
import { ITypeaheadOption, keyValueToDropdownOption } from "../../components/Form/TypeaheadField/TypeaheadField";
import TypeaheadFieldV2 from "../../components/Form/TypeaheadFieldV2/TypeaheadFieldV2";
import Toast from "../../components/Toast/Toast";
import * as S from "../../constants/StringConstants";
import { defaultBroker, IBroker } from "../../store/models/broker.model";
import { IRootState } from "../../store/reducers";
import {
  fetchBrokerByPrimaryEmail,
  fetchBrokerOptions,
  fetchCompanyAffiliate,
} from "../../store/reducers/brokerBuilder";
import { createBroker, fetchBroker, hideErrorToastBroker, updateBroker } from "../../store/reducers/brokers";
import { deleteBrokerLogo, fetchBrokerLogo, uploadBrokerLogo } from "../../store/reducers/brokersImages";
import { formatUSD } from "../../util/utils.currency";
import { addDefaultOption, isEmptyOrDefault, undefinedIfDefault } from "../../util/utils.defaultValues";
import * as P from "../../util/utils.validators";
import BrokerCommentsWidget from "././BrokerCommentsWidget/BrokerCommentsWidget";
import BrokerSecureDocumentsWidget from "././BrokerSecureDocsWidget/BrokerSecureDocumentsWidget";
import BrokerAffiliatesTable from "./BrokerAffiliatesTable/BrokerAffiliatesTable";
import BrokerAnalyzerWidget from "./BrokerAnalyzerWidget/BrokerAnalyzerWidget";
import "./BrokerBuilder.scss";
import BrokerEmailsTable from "./BrokerEmailsTable/BrokerEmailsTable";
import BrokerQuotesWidget from "./BrokerQuotesWidget/BrokerQuotesWidget";

interface IBrokerBuilderProps extends StateProps, DispatchProps, RouteComponentProps<{ id: string }> {}

interface BrokerBuilderState {
  logoUploading: boolean;
  uploadPercentage: number;
}

class BrokerBuilder extends React.Component<IBrokerBuilderProps, BrokerBuilderState> {
  state = {
    logoUploading: false,
    uploadPercentage: 0,
  };

  /** ---------------- Email helpers (FE-only fields used by the table) ---------------- */
  private EMAIL_RE = /\S+@\S+\.\S+/;

  /** FE table (emailRows/emailPrimaryIndex) -> BE brokerAdditionalEmails */
  private rowsToAdditionalEmails(values: any) {
    const rows: Array<{
      email?: string;
      isSecondary?: boolean;
      isCc?: boolean;
      isCaseStatus?: boolean;
      isRequester?: boolean;
    }> = get(values, "emailRows", []) || [];
    const primaryIndexStr = get(values, "emailPrimaryIndex");
    const primaryIndex = primaryIndexStr !== undefined && primaryIndexStr !== null ? Number(primaryIndexStr) : -1;

    const out: Array<{
      emailAddress: string;
      emailType: "Primary" | "Alternate" | "CC" | "Requester" | "UW";
    }> = [];

    rows.forEach((row, idx) => {
      const email = (row?.email || "").trim();
      if (!email) return;

      if (idx === primaryIndex) out.push({ emailAddress: email, emailType: "Primary" });
      if (row.isSecondary) out.push({ emailAddress: email, emailType: "Alternate" });
      if (row.isCc) out.push({ emailAddress: email, emailType: "CC" });
      if (row.isCaseStatus) out.push({ emailAddress: email, emailType: "UW" });
      if (row.isRequester) out.push({ emailAddress: email, emailType: "Requester" });
    });

    // de-dup entries (same email+type)
    const map = new Map<
      string,
      { emailAddress: string; emailType: "Primary" | "Alternate" | "CC" | "Requester" | "UW" }
    >();
    out.forEach(e => map.set(`${e.emailAddress}__${e.emailType}`, e));
    return Array.from(map.values());
  }

  /** BE brokerAdditionalEmails -> FE table (emailRows/emailPrimaryIndex) */

  private additionalEmailsToRows(adds: any[]) {
    type Row = {
      email: string;
      isSecondary?: boolean;
      isCc?: boolean;
      isCaseStatus?: boolean;
      isRequester?: boolean;
      __primary?: boolean;
    };

    const byEmail = new Map<string, Row>();

    (adds || []).forEach(e => {
      const email = (e?.emailAddress || "").trim();
      if (!email) return;

      const type = e?.emailType;

      // ↓ key change: ?? and explicit annotation
      const row: Row = byEmail.get(email) ?? {
        email,
        isSecondary: false,
        isCc: false,
        isCaseStatus: false,
        isRequester: false,
      };

      switch (type) {
        case "Primary":
          row.__primary = true;
          break;
        case "Alternate":
          row.isSecondary = true;
          break;
        case "CC":
          row.isCc = true;
          break;
        case "Requester":
          row.isRequester = true;
          break;
        case "UW":
          row.isCaseStatus = true;
          break;
        default:
          break;
      }
      byEmail.set(email, row);
    });

    const rows = Array.from(byEmail.values());
    const primaryIdx = rows.findIndex(r => r.__primary);
    rows.forEach(r => delete r.__primary);

    return {
      emailRows:
        rows.length > 0
          ? rows
          : [{ email: "", isSecondary: false, isCc: false, isCaseStatus: false, isRequester: false }],
      emailPrimaryIndex: primaryIdx >= 0 ? String(primaryIdx) : undefined,
    };
  }

  /** ---------------- Validation ---------------- */
  validate = (values: any) => {
    const errors: any = {};

    if (values.isBusinessOwner < 0) {
      errors["isBusinessOwner"] = S.FORM_FIELD_REQUIRED;
    }
    if (P.isEmpty(values.firstName)) {
      errors["firstName"] = S.FORM_FIELD_REQUIRED;
    }
    if (P.isEmpty(values.lastName)) {
      errors["lastName"] = S.FORM_FIELD_REQUIRED;
    }

    const howDidYouHearAboutUsId = get(values, "hearAboutTypeId");
    if (!howDidYouHearAboutUsId || howDidYouHearAboutUsId < 0) {
      set(errors, "hearAboutTypeId", S.FORM_FIELD_REQUIRED);
    }

    const phoneFields = ["officePhone", "cellPhone"];
    phoneFields.forEach(fieldName => {
      const phoneNumber = get(values, fieldName);
      const pattern = P.PHONE_NUMBER_REGEX;
      if (phoneNumber && !pattern.test(phoneNumber)) {
        set(errors, fieldName, S.FORM_FIELD_INVALID);
      }
    });

    const zipCode = get(values, "address.zip");
    const zipPattern = P.ZIP_CODE_REGEX;
    if (zipCode && !zipPattern.test(zipCode)) {
      set(errors, "address.zip", S.FORM_FIELD_INVALID);
    }

    const branchTypeID = get(values, "branchTypeID");
    if (isEmptyOrDefault(branchTypeID)) {
      set(errors, "branchTypeID", S.FORM_FIELD_REQUIRED);
    }

    // --- EMAIL TABLE (FieldArray: emailRows) ---
    {
      const rows = get(values, "emailRows", []) || [];
      const primaryIdxStr = get(values, "emailPrimaryIndex");

      // Always use an ARRAY for FieldArray errors
      const emailRowsErrors: any[] = [];

      // Array-level rule: if any row has an email, a valid primary must be selected
      const filledRows = rows.filter((r: any) => (r?.email || "").trim());
      if (filledRows.length > 0) {
        const primaryIdx = primaryIdxStr !== undefined && primaryIdxStr !== null ? Number(primaryIdxStr) : -1;
        const hasValidPrimary = primaryIdx >= 0 && rows[primaryIdx]?.email?.trim();
        if (!hasValidPrimary) {
          (emailRowsErrors as any)._error = S.FORM_FIELD_CHOOSE_PRIMARY_EMAIL;
        }
      }

      // Per-row validation
      rows.forEach((r: any, i: number) => {
        const email = (r?.email || "").trim();
        const anyFlag = r?.isSecondary || r?.isCc || r?.isCaseStatus || r?.isRequester;
        const rowError: any = {};

        if ((email && !this.EMAIL_RE.test(email)) || (anyFlag && !email)) {
          rowError.email = S.FORM_FIELD_INVALID;
        }

        if (Object.keys(rowError).length > 0) {
          emailRowsErrors[i] = rowError;
        }
      });

      // Only assign when there are per-row errors or an array-level _error
      if ((emailRowsErrors as any)._error || emailRowsErrors.length > 0) {
        set(errors, "emailRows", emailRowsErrors);
      }
    }

    return errors;
  };

  /** ---------------- Submit + lifecycle ---------------- */
  handleSubmit = async (values: any) => {
    const broker = this.formValuesToBroker(values);

    try {
      let brokerId = this.brokerID;

      if (brokerId) {
        this.fillBrokerEmptyValues(broker);
        await this.props.updateBroker(broker);
      } else {
        const response = await this.props.createBroker(broker);
        // @ts-ignore
        brokerId = response.value.data;
      }
      await this.props.saveBrokerAnalyzerPreference(brokerId, values.preferenceID);
    } catch (err) {
      return { [FORM_ERROR]: S.FORM_FIELD_SUBMIT_ERROR };
    }

    this.props.history.push("/brokers/all");
  };

  fillBrokerEmptyValues(broker) {
    if (typeof get(broker, "companyID") !== "number") {
      set(broker, "companyID", 0);
    }
    if (typeof get(broker, "affiliateID") !== "number") {
      set(broker, "affiliateID", undefined);
    }
  }

  componentDidMount() {
    this.props.fetchBrokerOptions();
    this.props.fetchAnalyzerPreferenceOptions();

    // check match for id param
    const id = this.brokerID;
    if (id) {
      this.props.fetchBroker(id.toString());
      this.props.fetchBrokerAnalyzerPreference(this.brokerID);
      this.props.fetchBrokerLogo(id);
    }

    this.props.history.listen(() => {
      this.formStateTemp = undefined;
    });
  }

  formValuesToBroker = (values: any) => {
    const primaryPhone = get(values, "primaryPhone");
    const cellPhone = get(values, "cellPhone");
    const isSMSTextEnabled = get(values, "isSMSTextEnabled");

    const brokerPhones = [
      {
        phoneNumber: get(values, "officePhone"),
        isPrimary: primaryPhone === S.BRO_PHONE_TYPE_OFFICE,
        phoneType: S.BRO_PHONE_TYPE_OFFICE,
      },
      {
        phoneNumber: cellPhone,
        isPrimary: primaryPhone === S.BRO_PHONE_TYPE_CELL,
        phoneType: S.BRO_PHONE_TYPE_CELL,
      },
    ].filter(x => x && x.phoneNumber);

    const brokerAdditionalEmails = this.rowsToAdditionalEmails(values);

    let affiliateIDValue = get(values, "affiliateID");
    let affiliateID;

    if (!isNaN(affiliateIDValue) && affiliateIDValue > 0) {
      affiliateID = affiliateIDValue;
    } else {
      affiliateID = undefined;
    }

    const brokerAffiliates = get(values, "brokerAffiliates", [])
      .filter(item => item?.affiliate?.value)
      .map(item => {
        return item.affiliateRemovedByUser
          ? item
          : {
              ...item,
              affiliateId: item.affiliate.value,
            };
      });

    let companyIDs = undefinedIfDefault(get(values, "companyID"));
    //  Default behavior for an empty new form is to have it as undefined
    let companyID = undefined;

    if (Array.isArray(companyIDs) && companyIDs.length > 0) {
      // form was pre-populated with a list of options
      companyID = companyIDs[0].value;
    } else if (Number.isInteger(companyIDs)) {
      // a single item has been selected
      companyID = companyIDs;
    }

    if (values.address) {
      values.address.stateID = undefinedIfDefault(values.address.stateID);
    }

    return {
      brokerID: get(values, "brokerID"),
      firstName: get(values, "firstName"),
      lastName: get(values, "lastName"),
      preferredName: get(values, "preferredName"),
      companyID: companyID,
      affiliateID: undefinedIfDefault(affiliateID),
      brokerAffiliates,
      branchTypeID: undefinedIfDefault(get(values, "branchTypeID")),
      hearAboutTypeId: undefinedIfDefault(get(values, "hearAboutTypeId")),
      isBusinessOwner: get(values, "isBusinessOwner") === "1" ? true : false,
      brokerPhones,
      isSMSTextEnabled,
      brokerAdditionalEmails,
      address: get(values, "address"),
      notes: get(values, "notes"),
      middleName: get(values, "middleName"),
      agentSSNorEIN: get(values, "taxID"),
      taxTypeId: get(values, "taxTypeID"),
      dob: get(values, "dob"),
    };
  };

  brokerToFormValues = (broker: IBroker, affiliates: ITypeaheadOption[]) => {
    const brokerPhones = get(broker, "brokerPhones") || [];
    const brokerAdditionalEmails = get(broker, "brokerAdditionalEmails") || [];
    let officePhoneItem = brokerPhones.find(x => x.phoneType === "Office" /* 1 S.BRO_PHONE_TYPE_OFFICE*/);

    const primaryAffiliate = get(broker, "affiliateID");

    const brokerAffiliates = get(broker, "brokerAffiliates", []).map(item => {
      const aff = affiliates.find(a => a.value === item.affiliateId);
      return {
        affiliate: aff,
        ...item,
      };
    });

    let officePhone = get(officePhoneItem, "phoneNumber") || get(broker, "officePhone");
    let cellPhone =
      get(
        brokerPhones.find(x => x.phoneType === "Cell"),
        "phoneNumber",
      ) || get(broker, "cellPhone");

    const emailsTableData = this.additionalEmailsToRows(brokerAdditionalEmails);

    return {
      brokerID: get(broker, "brokerID"),
      firstName: get(broker, "firstName"),
      lastName: get(broker, "lastName"),
      preferredName: get(broker, "preferredName"),
      companyFreeTypeName: get(broker, "companyFreeTypeName"),
      affiliateFreeTypeName: get(broker, "affiliateFreeTypeName"),
      companyID: get(broker, "companyID"),
      affiliateID: primaryAffiliate,
      brokerAffiliates,
      isSMSTextEnabled: get(broker, "isSMSTextEnabled"),
      branchTypeID: get(broker, "branchTypeID"),
      hearAboutTypeId: get(broker, "hearAboutTypeId"),
      isBusinessOwner: get(broker, "isBusinessOwner") ? 1 : 0,
      address: get(broker, "address"),
      notes: get(broker, "notes"),
      officePhone: officePhone,
      cellPhone: cellPhone,
      primaryPhone: officePhoneItem && officePhoneItem.isPrimary ? S.BRO_PHONE_TYPE_OFFICE : S.BRO_PHONE_TYPE_CELL,

      // FE-only fields for the table (NOT sent to BE)
      emailRows: emailsTableData.emailRows,
      emailPrimaryIndex: emailsTableData.emailPrimaryIndex,

      middleName: get(broker, "middleName"),
      taxID: get(broker, "agentSSNorEIN"),
      taxTypeID: get(broker, "taxTypeId"),
      dob: get(broker, "dob"),
      preferenceID: this.props.selectedPreference,
    };
  };

  getPrimaryPhoneLabel(form: FormApi, key: string) {
    const field = form.getFieldState("primaryPhone");
    if (!field) {
      return "";
    }

    return field.value === key ? S.BRO_PRIMARY_LABEL : "";
  }

  defaultBroker = undefined;
  get broker() {
    if (this.brokerID) {
      return this.props.brokers.map[this.props.match.params.id];
    } else {
      if (!this.defaultBroker) {
        this.defaultBroker = defaultBroker;
      }

      return this.defaultBroker;
    }
  }

  get brokerID() {
    const id = parseInt(this.props.match.params.id, 10);

    return id && !isNaN(id) ? id : null;
  }

  formStateTemp = undefined;
  formStateHandler = createDecorator({
    field: /./,
    updates: (value, name, allValues, previousValues) => {
      this.formStateTemp = allValues;
      return {};
    },
  });

  onBrokerFileSelectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0];
    const id = this.brokerID;
    if (!file || !id) return;

    const reset = () => {
      if (e.target) e.target.value = "";
    };

    const formData = new FormData();
    formData.append("file", file, file.name);

    this.setState({ logoUploading: true, uploadPercentage: 0 });

    this.props.uploadBrokerLogo(id, formData, (percent: number) => {
      this.setState({ uploadPercentage: percent });
    });

    reset();
  };

  onBrokerFileDelete = () => {
    const id = this.brokerID;
    if (!id) return;
    this.props.deleteBrokerLogo(id);
  };

  render() {
    const { dropdownOptions, brokerImages, companyAffiliate, brokers, hideErrorToastBroker } = this.props;
    const officePhoneMask = { name: "phone-1", parse: "(999) 999-9999.999999" };
    const phoneMask = { name: "phone-1", parse: "(999) 999-9999" };

    const image = brokerImages.images.find(x => x.brokerID === this.brokerID);
    const logoBrokerUrl = image?.imageUrl;

    let totalPremium = 0;

    const brokerProductionNodes = (
      this.broker && this.broker.brokerProduction && this.broker.brokerProduction.length
        ? this.broker.brokerProduction
        : defaultBroker.brokerProduction
    ).reduce((memo, item, index) => {
      totalPremium += item.paidPremium || 0;

      return memo.concat([
        index === 0 && <Row className="card-separator" key={`separator_${index}`} />,
        <Row className="card-row card-values" key={`prod_${index}`}>
          <Col sm="4">{item.carrierTypeDesc || S.BRO_CARD_NOTHING_YET}</Col>
          <Col sm="4">{item.appCount || S.BRO_CARD_APP_COUNT_DEFAULT_NR}</Col>
          <Col sm="4" className="left">
            {item.paidPremium ? `${formatUSD(item.paidPremium)}` : S.BRO_CARD_YTD_TOTAL_DEFAULT_NR}
          </Col>
        </Row>,
      ]);
    }, []);

    const defaultValues = {
      affiliateID: undefined,
      branchTypeID: undefined,
      companyID: undefined,
      primaryPhone: S.BRO_PHONE_TYPE_OFFICE,
      ...this.formStateTemp,
    };

    const affiliateList = (get(dropdownOptions, "affiliateList") || []).map(keyValueToDropdownOption);

    const branchList = addDefaultOption(S.DD_BUILDER_LABEL_BRANCH, get(dropdownOptions, "branchList", []));

    const rbmList = addDefaultOption("", get(dropdownOptions, "rbmList", []));
    const stateList = addDefaultOption("", get(dropdownOptions, "selectStateList", []));
    const businessOwnerList = [
      {
        key: "",
        value: -1,
      },
      {
        key: "Yes",
        value: 1,
      },
      {
        key: "No",
        value: 0,
      },
    ];

    const taxTypeList = [
      {
        key: "",
        value: 0,
      },
      {
        key: S.BRO_TAX_ID_TYPE_LABEL_SSN,
        value: 1,
      },
      {
        key: S.BRO_TAX_ID_TYPE_LABEL_EIN,
        value: 2,
      },
    ];

    const initialValues =
      this.broker && affiliateList ? this.brokerToFormValues(this.broker, affiliateList) : defaultValues;
    const hearAboutUsFullList = addDefaultOption(
      S.DD_BUILDER_LABEL_HOWDIDYOUHEARABOUTUS,
      get(dropdownOptions, "hearAboutUsList", []),
    );
    return (
      <div>
        <Toast message={S.LOADING} onClose={() => {}} open={this.props.loading} />
        <Toast
          message={brokers.errorMessage}
          onClose={() => {
            hideErrorToastBroker();
          }}
          open={brokers.error}
        />
        <Toast
          message={S.UPLOADING(this.state.uploadPercentage)}
          onClose={() => this.setState({ logoUploading: false })}
          open={this.state.logoUploading}
        />

        <FinalForm
          validate={this.validate}
          onSubmit={this.handleSubmit}
          initialValues={initialValues}
          decorators={[this.formStateHandler]}
          mutators={{ ...arrayMutators }}
          render={({ handleSubmit, submitting, invalid, values, form }) => {
            // If viewing an existing broker with an affiliate always select that affiliate.
            // If there is no existing broker affiliate use the affiliate of the selected company.
            let affiliate = undefined;
            const brokerAffiliateID = get(values, "affiliateID");
            if (brokerAffiliateID) {
              affiliate = affiliateList.find(a => a.value === brokerAffiliateID);
              defaultValues.affiliateID = affiliate ? affiliate.value : 0;
            } else if (companyAffiliate) {
              affiliate = affiliateList.find(a => a.value === get(companyAffiliate, "affiliateID"));
              defaultValues.affiliateID = affiliate ? affiliate.value : 0;
            }

            // filter companies with these affilates
            var unfilteredCompanyList = get(dropdownOptions, "companyList", []);
            const companyList = addDefaultOption(
              S.DD_BUILDER_LABEL_COMPANY,
              unfilteredCompanyList.sort(function (a, b) {
                return a.key.localeCompare(b.key);
              }),
            ).map(keyValueToDropdownOption);

            let selectedCompany = undefined;
            if (values.companyID) {
              selectedCompany = companyList.find(c => c.value === values.companyID);
            }

            return (
              <form onSubmit={handleSubmit}>
                <div className="broker-builder__form">
                  <section className="broker-builder__wrap">
                    <DetailContainer>
                      <div className="broker-builder__detail-form">
                        <Col className="app-form-header">
                          <h4 className="form-title">{S.BRO_INFO_TITLE}</h4>
                        </Col>

                        <Row>
                          <Col sm="3">
                            <TextField name="firstName" key="broker.firstName-key" label={S.BRO_FIRST_NAME_LABEL} />
                          </Col>
                          <Col sm="3">
                            <TextField name="middleName" label={S.BRO_MIDDLE_NAME_LABEL} />
                          </Col>
                          <Col sm="3">
                            <TextField name="lastName" key="broker.lastName-key" label={S.BRO_LAST_NAME_LABEL} />
                          </Col>
                          <Col sm="3">
                            <TextField
                              name="preferredName"
                              key="broker.preferredName-key"
                              label={S.BRO_PREF_NAME_LABEL}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col sm="3">
                            <SimpleDatePickerField name="dob" label={S.BRO_DOB_LABEL} isFormMode />
                          </Col>
                          <Col>
                            <FormSpy
                              render={({ form }) => (
                                <Row>
                                  <Col sm="4" className="broker-builder__phone-radio">
                                    <div className="broker-builder__short-div">
                                      <TextField
                                        name="officePhone"
                                        key="broker.officePhone-key"
                                        label={S.BRO_PHONE_TYPE_PHONE}
                                        type="tel"
                                        parse={formatString(officePhoneMask.parse)}
                                      />
                                    </div>
                                    <div className="broker-builder__radio_wrap">
                                      <label className="radio-small-wrapper">
                                        <RadioButton label="" name="primaryPhone" value={S.BRO_PHONE_TYPE_OFFICE} />{" "}
                                        {this.getPrimaryPhoneLabel(form, S.BRO_PHONE_TYPE_OFFICE)}
                                      </label>
                                    </div>
                                  </Col>
                                  <Col sm="4" className="broker-builder__phone-radio">
                                    <div className="broker-builder__short-div">
                                      <TextField
                                        name="cellPhone"
                                        key="broker.cellPhone-key"
                                        label={S.BRO_PHONE_TYPE_CELL}
                                        type="tel"
                                        parse={formatString(phoneMask.parse)}
                                      />
                                    </div>
                                    <div className="broker-builder__radio_wrap">
                                      <label className="radio-small-wrapper">
                                        <RadioButton label="" name="primaryPhone" value={S.BRO_PHONE_TYPE_CELL} />{" "}
                                        {this.getPrimaryPhoneLabel(form, S.BRO_PHONE_TYPE_CELL)}
                                      </label>
                                    </div>
                                  </Col>
                                </Row>
                              )}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col sm="3">
                            <TextField
                              name="address.streetAddress"
                              key="broker.streetAddress-key"
                              label={S.BRO_STREET_ADDRESS_LABEL}
                              multiline
                            />
                          </Col>
                          <Col sm="3">
                            <TextField name="address.city" key="broker.city-key" label={S.BRO_CITY_LABEL} />
                          </Col>
                          <Col sm="6">
                            <Container className="bootsrap-container-override">
                              <Row>
                                <Col>
                                  <SingleSelectField
                                    name="address.stateID"
                                    key="broker.state-key"
                                    label={S.BRO_STATE_LABEL}
                                    options={stateList}
                                  />
                                </Col>
                                <Col>
                                  <TextField name="address.zip" key="broker.zip-key" label={S.BRO_ZIP_LABEL} />
                                </Col>
                              </Row>
                            </Container>
                          </Col>
                        </Row>

                        <Row>
                          <Col>
                            <h4 className="broker-builder__section-title">{S.BRO_EMAILS_TITLE}</h4>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <BrokerEmailsTable form={form} />
                          </Col>
                        </Row>

                        <hr />
                        <Row>
                          <Col>
                            <h4 className="form-title">{S.BRO_AFFILIATE_TITLE}</h4>
                          </Col>
                        </Row>

                        <Row>
                          <h4 className="broker-builder__section-title">{S.BRO_AFFILIATE_SUB_TITLE}</h4>
                        </Row>

                        <Row className="broker-builder__affiliates">
                          <Col>
                            <BrokerAffiliatesTable
                              form={form}
                              values={values}
                              affiliateList={affiliateList}
                              dropdownOptions={dropdownOptions}
                            />

                            <OnChange name="companyID">
                              {async value => {
                                const companyID = value;
                                if (companyID) {
                                  const metaCompanyData = dropdownOptions.companyList.find(x => x.value === companyID);

                                  try {
                                    form.change("companyID", companyID);
                                    form.change("branchTypeID", metaCompanyData.branchTypeID);

                                    const hasThisBrokerAlready = values.brokerAffiliates.find(
                                      aff => aff.affiliateId === metaCompanyData.affiliateId,
                                    );

                                    if (!hasThisBrokerAlready && metaCompanyData.affiliateId) {
                                      form.change("affiliateID", metaCompanyData.affiliateId);

                                      const newAff = dropdownOptions.affiliateList.find(
                                        x => x.value === metaCompanyData.affiliateId,
                                      );

                                      form.mutators.push("brokerAffiliates", {
                                        affiliate: { label: newAff.key, value: newAff.value },
                                        affiliateId: metaCompanyData.affiliateId,
                                      });
                                    }
                                  } catch (e) {
                                    console.log("exception:", e);
                                  }
                                }
                              }}
                            </OnChange>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="3">
                            <TypeaheadFieldV2
                              name="companyID"
                              label={S.BRO_COMPANY_LABEL}
                              key="broker.companyID-key"
                              options={companyList}
                              selected={selectedCompany}
                            />
                          </Col>
                          <Col sm="3">
                            <TextField
                              name="brokerID"
                              key="broker.brokerID-key"
                              label={S.BRO_BROKER_ID_LABEL}
                              disabled={true}
                            />
                          </Col>
                          <Col sm="3">
                            <TextField
                              name="affiliateFreeTypeName"
                              key="broker.affiliateFreeTypeName-key"
                              label={S.BRO_AFFILIATE_PUBLIC_LABEL}
                              disabled="disabled"
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="3">
                            <SingleSelectField
                              name="branchTypeID"
                              key="broker.branchTypeID-key"
                              label={S.BRO_BRANCH_LABEL}
                              options={branchList}
                            />
                          </Col>
                          <Col sm="3">
                            <SingleSelectField name="taxTypeID" label={S.BRO_TAX_ID_TYPE_LABEL} options={taxTypeList} />
                          </Col>
                          <Col sm="3">
                            <TextField name="taxID" label={S.BRO_TAX_ID_LABEL} />
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="3">
                            <SingleSelectField
                              name="isBusinessOwner"
                              key="broker.branchTypeID-key"
                              label={S.BRO_BUSINESS_OWNER_LABEL}
                              options={businessOwnerList}
                            />
                          </Col>
                          <Col sm="3">
                            <SingleSelectField
                              name="hearAboutTypeId"
                              key="broker.hearAboutTypeId-key"
                              label={S.BRO_HOW_DID_YOU_HEAR_ABOUT_US_LABEL}
                              options={hearAboutUsFullList}
                            />
                          </Col>
                        </Row>

                        <hr />

                        <Row>
                          <Col className="app-form-header">
                            <h4 className="form-title">{S.BRO_NOTES_TITLE}</h4>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="6">
                            <TextField name="notes" key="broker.notes-key" label={S.BRO_NOTES_LABEL} multiline />
                          </Col>

                          <Col sm="3">
                            <span className="upload-logo-label">{S.BRO_UPLOAD_LOGO_LABEL}</span>
                            <div className="logo-upload-wrapper">
                              <label className="logo-upload-container" htmlFor="broker-logo-upload">
                                {logoBrokerUrl ? (
                                  <img src={logoBrokerUrl} alt="Broker Logo" className="logo-preview" />
                                ) : (
                                  <div className="logo-placeholder">
                                    <CustomIcon name={IconName.UploadCloud} size="24px" />
                                  </div>
                                )}
                              </label>

                              {logoBrokerUrl && (
                                <div>
                                  <button
                                    type="button"
                                    className="button-v2 button-v2__primary tiny button-flex"
                                    onClick={this.onBrokerFileDelete}>
                                    <span>{S.BRO_DELETE_LOGO_BUTTON}</span>
                                  </button>
                                </div>
                              )}

                              <input
                                id="broker-logo-upload"
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={this.onBrokerFileSelectionChange}
                              />
                            </div>
                          </Col>
                          <Col sm="3">
                            <CheckboxField name="isSMSTextEnabled" label={S.UDM_SEND_SMS} labelPosition="above" />
                          </Col>
                        </Row>

                        <Row>
                          <Col>
                            <div className="broker-builder__top-bar">
                              <div className="buttons">
                                <button
                                  className="button-v2 button-v2__secondary"
                                  onClick={() => this.props.history.goBack()}>
                                  {S.BRO_CANCEL_BUTTON}
                                </button>

                                <button className="button-v2 button-v2__primary" type="submit" disabled={submitting}>
                                  {submitting ? <Spinner color="light" /> : S.BRO_SUBMIT_BUTTON}
                                </button>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </DetailContainer>
                  </section>

                  <aside>
                    <CollapsibleCard
                      title={S.BRO_CARD_TITLE}
                      iconName={IconName.BrokerBuilderProduction}
                      defaultOpen={true}>
                      <Container>
                        <Row className="card-row card-table-header">
                          <Col>{S.BRO_CARD_CARRIER}</Col>
                          <Col>{S.BRO_CARD_APP_COUNT}</Col>
                          <Col>{S.BRO_CARD_YTD_TOTAL}</Col>
                        </Row>
                        {brokerProductionNodes}
                        {totalPremium > 0}
                        {totalPremium > 0 && (
                          <Row className="card-row card-totals">
                            <Col>{S.BRO_CARD_TOTAL_LABEL}</Col>
                            <Col />
                            <Col>{formatUSD(totalPremium)}</Col>
                          </Row>
                        )}
                      </Container>
                    </CollapsibleCard>
                    <div>
                      <BrokerAnalyzerWidget />
                    </div>
                    <div>
                      <BrokerQuotesWidget />
                    </div>
                    <div>
                      <BrokerSecureDocumentsWidget />
                    </div>
                    <div>
                      <BrokerCommentsWidget />
                    </div>
                  </aside>
                </div>
                <FormSpy
                  subscription={{ values: true, valid: true }}
                  onChange={state => {
                    const { values, valid } = state;

                    this.defaultBroker = values;
                  }}
                />
              </form>
            );
          }}
        />
      </div>
    );
  }
}

const mapStateToProps = ({
  brokers,
  brokerImages,
  brokerAnalyzerPreferences: { selectedPreference },
  brokerBuilder: {
    loading,
    companyAffiliate,
    existentBrokerIdByEmail,
    isAfiliateRequestInProgress,
    ...dropdownOptions
  },
}: IRootState) => ({
  brokers,
  brokerImages,
  companyAffiliate,
  selectedPreference,
  isAfiliateRequestInProgress,
  dropdownOptions,
  existentBrokerIdByEmail,
  loading,
});

const mapDispatchToProps = {
  createBroker,
  fetchBrokerOptions,
  fetchBroker,
  updateBroker,
  uploadBrokerLogo,
  saveBrokerAnalyzerPreference,
  deleteBrokerLogo,
  fetchBrokerLogo,
  fetchCompanyAffiliate,
  fetchBrokerByPrimaryEmail,
  fetchBrokerAnalyzerPreference,
  fetchAnalyzerPreferenceOptions,
  hideErrorToastBroker,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(BrokerBuilder);
